// src/components/FormCampaign.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import { useNavigate, useParams } from 'react-router-dom';
import { createCampaign, getCampaignById, updateCampaign, getAllProducts } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import { normalizeSearchTerm, PRODUCT_SEARCH_HINT, processarBuscaProdutoEnter, termoBuscaApi } from '../utils/productSearch';
import { toDatetimeLocalValue, fromDatetimeLocalValue } from '../utils/datetimeLocal';
import { precoProdutoCadastrado } from '../utils/productPrice';
import { moverItemLista } from '../utils/listOrder';
import ResponsiveTable from './common/ResponsiveTable';

const TIPOS = { PROMOCAO: 1, CASHBACK: 2 };

const DIAS = [
  ['segunda', 'Segunda'],
  ['terca', 'Terça'],
  ['quarta', 'Quarta'],
  ['quinta', 'Quinta'],
  ['sexta', 'Sexta'],
  ['sabado', 'Sábado'],
  ['domingo', 'Domingo'],
];

const toNumberOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

const toPercentOrNull = (v) => {
  const n = toNumberOrNull(v);
  if (n === null) return null;
  return Math.max(0, Math.min(100, n));
};

const fmtMoeda = (v) =>
  Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export default function FormCampaign({
  isEditMode = false,
  campaignId = null,
  embedded = false,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate();
  const params = useParams();

  const id = campaignId ?? params.id;
  const editing = isEditMode || !!campaignId;

  const [aba, setAba] = useState(0);

  const [form, setForm] = useState({
    estabelecimentoId: 1,
    ativo: true,
    descricao: '',
    tipo: TIPOS.PROMOCAO,
    cashbackPercent: '',
    vigenciaInicio: new Date().toISOString(),
    vigenciaFim: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString(),
    segunda: true, segundaInicio: null, segundaFim: null,
    terca: true, tercaInicio: null, tercaFim: null,
    quarta: true, quartaInicio: null, quartaFim: null,
    quinta: true, quintaInicio: null, quintaFim: null,
    sexta: true, sextaInicio: null, sextaFim: null,
    sabado: false, sabadoInicio: null, sabadoFim: null,
    domingo: false, domingoInicio: null, domingoFim: null,
    itens: [],
  });

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [prodSearch, setProdSearch] = useState('');
  const [prodResults, setProdResults] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const idsNaCampanha = useMemo(
    () => new Set(form.itens.map((i) => i.produtoId)),
    [form.itens],
  );

  const buscarProdutos = async (termoOverride) => {
    const termo = normalizeSearchTerm(
      termoOverride !== undefined ? termoOverride : prodSearch,
    );
    try {
      setBuscando(true);
      const { data } = await getAllProducts({
        indice: 1,
        qtde: 50,
        termo,
        estabelecimentoId: form.estabelecimentoId || '',
      });
      setProdResults(data.items || []);
      setSelectedIds(new Set());
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao buscar produtos.'));
    } finally {
      setBuscando(false);
    }
  };

  const onEnterBuscaProduto = async () => {
    const termo = prodSearch;
    if (!termoBuscaApi(termo)) return;
    try {
      setBuscando(true);
      setError('');
      const resultado = await processarBuscaProdutoEnter({
        termo,
        estabelecimentoId: form.estabelecimentoId,
        qtde: 50,
      });
      if (resultado.tipo === 'adicionar') {
        addItem(resultado.produto);
        setProdSearch('');
        setProdResults([]);
        setSelectedIds(new Set());
        setAba(2);
        return;
      }
      if (resultado.tipo === 'nao_encontrado') {
        setError(resultado.mensagem);
        setProdResults([]);
        return;
      }
      if (resultado.tipo === 'lista') {
        setProdResults(resultado.produtos);
        setSelectedIds(new Set());
      }
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao buscar produtos.'));
    } finally {
      setBuscando(false);
    }
  };

  useEffect(() => {
    buscarProdutos('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.estabelecimentoId]);

  const fechar = () => {
    if (onCancel) onCancel();
    else navigate('/campaigns');
  };

  useEffect(() => {
    const fetch = async () => {
      if (!editing) return;
      try {
        const { data } = await getCampaignById(id);
        setForm({
          estabelecimentoId: data.estabelecimentoId,
          ativo: data.ativo,
          descricao: data.descricao,
          tipo: Number(data.tipo) || TIPOS.PROMOCAO,
          cashbackPercent: data.cashbackPercent ?? '',
          vigenciaInicio: data.vigenciaInicio,
          vigenciaFim: data.vigenciaFim,
          segunda: data.segunda, segundaInicio: data.segundaInicio, segundaFim: data.segundaFim,
          terca: data.terca, tercaInicio: data.tercaInicio, tercaFim: data.tercaFim,
          quarta: data.quarta, quartaInicio: data.quartaInicio, quartaFim: data.quartaFim,
          quinta: data.quinta, quintaInicio: data.quintaInicio, quintaFim: data.quintaFim,
          sexta: data.sexta, sextaInicio: data.sextaInicio, sextaFim: data.sextaFim,
          sabado: data.sabado, sabadoInicio: data.sabadoInicio, sabadoFim: data.sabadoFim,
          domingo: data.domingo, domingoInicio: data.domingoInicio, domingoFim: data.domingoFim,
          itens: (data.itens || []).map((i) => ({
            produtoId: i.produtoId,
            descricao: i.produtoDescricao ?? i.descricao,
            codigo: i.produtoCodigo ?? i.codigo,
            ean: i.ean,
            precoPromocional: i.precoPromocional ?? '',
            cashbackPercent: i.cashbackPercent ?? '',
          })),
        });
      } catch (e) {
        setError(getApiErrorMessage(e, 'Erro ao carregar a campanha.'));
      }
    };
    fetch();
  }, [id, editing]);

  const addItem = (p) => {
    if (idsNaCampanha.has(p.id)) return;
    const precoRef = precoProdutoCadastrado(p, form.estabelecimentoId);
    setForm((prev) => ({
      ...prev,
      itens: [
        ...prev.itens,
        {
          produtoId: p.id,
          descricao: p.descricao,
          codigo: p.codigo,
          ean: p.ean,
          precoPromocional: precoRef,
          precoCadastrado: precoRef,
          cashbackPercent: '',
        },
      ],
    }));
  };

  const addItensSelecionados = () => {
    const novos = prodResults.filter(
      (p) => selectedIds.has(p.id) && !idsNaCampanha.has(p.id),
    );
    if (novos.length === 0) return;
    setForm((prev) => ({
      ...prev,
      itens: [
        ...prev.itens,
        ...novos.map((p) => {
          const precoRef = precoProdutoCadastrado(p, form.estabelecimentoId);
          return {
            produtoId: p.id,
            descricao: p.descricao,
            codigo: p.codigo,
            ean: p.ean,
            precoPromocional: precoRef,
            precoCadastrado: precoRef,
            cashbackPercent: '',
          };
        }),
      ],
    }));
    setSelectedIds(new Set());
    setAba(2);
  };

  const toggleSelect = (produtoId) => {
    if (idsNaCampanha.has(produtoId)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(produtoId)) next.delete(produtoId);
      else next.add(produtoId);
      return next;
    });
  };

  const toggleSelectAllVisiveis = () => {
    const elegiveis = prodResults.filter((p) => !idsNaCampanha.has(p.id));
    const todosMarcados = elegiveis.length > 0 && elegiveis.every((p) => selectedIds.has(p.id));
    if (todosMarcados) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(elegiveis.map((p) => p.id)));
  };

  const moverItem = (index, delta) => {
    setForm((prev) => ({
      ...prev,
      itens: moverItemLista(prev.itens, index, delta),
    }));
  };

  const removeItem = (produtoId) => {
    setForm((prev) => ({
      ...prev,
      itens: prev.itens.filter((i) => i.produtoId !== produtoId),
    }));
  };

  const headerFmt = useMemo(() => ({
    d: toDatetimeLocalValue,
  }), []);

  const copiarCashbackParaItens = () => {
    const pct = toPercentOrNull(form.cashbackPercent);
    if (pct === null) return;
    setForm((prev) => ({
      ...prev,
      itens: prev.itens.map((i) => ({ ...i, cashbackPercent: String(pct) })),
    }));
  };

  const salvar = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      if (!form.descricao?.trim()) throw new Error('Descrição é obrigatória.');
      if (new Date(form.vigenciaInicio) > new Date(form.vigenciaFim)) {
        throw new Error('Vigência inválida.');
      }

      if (form.tipo === TIPOS.PROMOCAO) {
        const algumTemPreco = form.itens.some((i) => toNumberOrNull(i.precoPromocional) !== null);
        if (!algumTemPreco) throw new Error('Informe ao menos um preço promocional nos itens.');
      } else if (form.tipo === TIPOS.CASHBACK) {
        const cashbackCamp = toPercentOrNull(form.cashbackPercent);
        const algumItemTemPct = form.itens.some((i) => toPercentOrNull(i.cashbackPercent) !== null);
        if (cashbackCamp === null && !algumItemTemPct) {
          throw new Error('Defina o Cashback % na campanha ou em pelo menos um item.');
        }
      }

      const payload = {
        estabelecimentoId: form.estabelecimentoId,
        ativo: form.ativo,
        descricao: form.descricao.trim(),
        tipo: Number(form.tipo),
        cashbackPercent: toPercentOrNull(form.cashbackPercent),
        vigenciaInicio: form.vigenciaInicio,
        vigenciaFim: form.vigenciaFim,
        segunda: form.segunda, segundaInicio: form.segundaInicio, segundaFim: form.segundaFim,
        terca: form.terca, tercaInicio: form.tercaInicio, tercaFim: form.tercaFim,
        quarta: form.quarta, quartaInicio: form.quartaInicio, quartaFim: form.quartaFim,
        quinta: form.quinta, quintaInicio: form.quintaInicio, quintaFim: form.quintaFim,
        sexta: form.sexta, sextaInicio: form.sextaInicio, sextaFim: form.sextaFim,
        sabado: form.sabado, sabadoInicio: form.sabadoInicio, sabadoFim: form.sabadoFim,
        domingo: form.domingo, domingoInicio: form.domingoInicio, domingoFim: form.domingoFim,
        itens: form.itens.map((i) => ({
          produtoId: i.produtoId,
          precoPromocional: toNumberOrNull(i.precoPromocional),
          cashbackPercent: toPercentOrNull(i.cashbackPercent),
        })),
      };

      if (editing) await updateCampaign(id, payload);
      else await createCampaign(payload);

      if (onSaved) onSaved(editing ? 'Campanha atualizada' : 'Campanha criada');
      else navigate('/campaigns');
    } catch (e) {
      setError(getApiErrorMessage(e, 'Erro ao salvar a campanha.'));
    } finally {
      setSaving(false);
    }
  };

  const isCashback = Number(form.tipo) === TIPOS.CASHBACK;
  const qtdSelecionados = selectedIds.size;
  const elegiveisVisiveis = prodResults.filter((p) => !idsNaCampanha.has(p.id));
  const todosVisiveisMarcados =
    elegiveisVisiveis.length > 0 && elegiveisVisiveis.every((p) => selectedIds.has(p.id));

  return (
    <Box
      sx={{ p: embedded ? 0 : 3, maxWidth: embedded ? 'none' : 1100, mx: 'auto' }}
      component="form"
      onSubmit={salvar}
    >
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: 'primary.main', fontWeight: 900, mb: 2 }}>
          {editing ? 'Editar Campanha' : 'Nova Campanha'}
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={aba}
          onChange={(_, v) => setAba(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}
        >
          <Tab label="Geral" />
          <Tab label="Horários" />
          <Tab
            label={(
              <Stack direction="row" spacing={1} alignItems="center">
                <span>Produtos</span>
                {form.itens.length > 0 && (
                  <Chip size="small" label={form.itens.length} color="primary" />
                )}
              </Stack>
            )}
          />
        </Tabs>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          {aba === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Descrição da campanha"
                  value={form.descricao}
                  onChange={(e) => setForm({ ...form, descricao: e.target.value })}
                  fullWidth
                  required
                  placeholder="Ex.: Promoção refrigerantes verão"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Tipo
                </Typography>
                <RadioGroup
                  row
                  value={String(form.tipo)}
                  onChange={(e) => setForm({ ...form, tipo: Number(e.target.value) })}
                >
                  <FormControlLabel
                    value={String(TIPOS.PROMOCAO)}
                    control={<Radio />}
                    label="Promoção (preço por item)"
                  />
                  <FormControlLabel
                    value={String(TIPOS.CASHBACK)}
                    control={<Radio />}
                    label="Cashback (%)"
                  />
                </RadioGroup>
              </Grid>

              {isCashback && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Cashback % da campanha"
                      value={form.cashbackPercent}
                      onChange={(e) => setForm({ ...form, cashbackPercent: e.target.value })}
                      fullWidth
                      inputProps={{ inputMode: 'decimal', min: 0, max: 100, step: '0.01' }}
                      InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                      helperText="Opcional se definir % por item na aba Produtos"
                    />
                  </Grid>
                  <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Tooltip title="Copiar o % da campanha para todos os itens">
                      <Button variant="outlined" onClick={copiarCashbackParaItens} fullWidth sx={{ mt: { md: 1 } }}>
                        Aplicar % nos itens
                      </Button>
                    </Tooltip>
                  </Grid>
                </>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  label="Vigência início"
                  type="datetime-local"
                  value={headerFmt.d(form.vigenciaInicio)}
                  onChange={(e) => setForm({ ...form, vigenciaInicio: fromDatetimeLocalValue(e.target.value) })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Vigência fim"
                  type="datetime-local"
                  value={headerFmt.d(form.vigenciaFim)}
                  onChange={(e) => setForm({ ...form, vigenciaFim: fromDatetimeLocalValue(e.target.value) })}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.ativo}
                      onChange={(e) => setForm({ ...form, ativo: e.target.checked })}
                    />
                  )}
                  label="Campanha ativa"
                />
              </Grid>
            </Grid>
          )}

          {aba === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Marque os dias em que a campanha vale e, se quiser, restrinja o horário.
                Deixe início e fim vazios para valer o dia inteiro.
              </Typography>
              <ResponsiveTable variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: 48 }} />
                      <TableCell><strong>Dia</strong></TableCell>
                      <TableCell><strong>Início</strong></TableCell>
                      <TableCell><strong>Fim</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {DIAS.map(([key, label]) => (
                      <TableRow key={key} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={form[key]}
                            onChange={(e) => setForm({ ...form, [key]: e.target.checked })}
                          />
                        </TableCell>
                        <TableCell>{label}</TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={form[`${key}Inicio`] ?? ''}
                            onChange={(e) => setForm({ ...form, [`${key}Inicio`]: e.target.value || null })}
                            disabled={!form[key]}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="time"
                            size="small"
                            value={form[`${key}Fim`] ?? ''}
                            onChange={(e) => setForm({ ...form, [`${key}Fim`]: e.target.value || null })}
                            disabled={!form[key]}
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ResponsiveTable>
            </Box>
          )}

          {aba === 2 && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                  Buscar e selecionar produtos
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Digite parte do nome (ex.: <em>coca</em>), marque os itens desejados e adicione em lote.
                  Com leitor de barras, bipe e pressione Enter para incluir o produto automaticamente.
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                  <TextField
                    size="small"
                    placeholder={PRODUCT_SEARCH_HINT}
                    value={prodSearch}
                    onChange={(e) => setProdSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void onEnterBuscaProduto();
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    fullWidth
                  />
                  <Button variant="contained" onClick={() => buscarProdutos()} disabled={buscando}>
                    {buscando ? 'Buscando...' : 'Buscar'}
                  </Button>
                </Stack>

                {prodResults.length > 0 && (
                  <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="text" onClick={toggleSelectAllVisiveis}>
                      {todosVisiveisMarcados ? 'Desmarcar todos' : 'Marcar todos visíveis'}
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<PlaylistAddIcon />}
                      disabled={qtdSelecionados === 0}
                      onClick={addItensSelecionados}
                    >
                      Adicionar selecionados ({qtdSelecionados})
                    </Button>
                  </Stack>
                )}

                {prodResults.length > 0 ? (
                  <Paper variant="outlined" sx={{ maxHeight: 280, overflow: 'auto' }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox" sx={{ width: 48 }} />
                          <TableCell>Código</TableCell>
                          <TableCell>Descrição</TableCell>
                          <TableCell align="right">Preço cadastro</TableCell>
                          <TableCell align="right" sx={{ width: 100 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {prodResults.map((p) => {
                          const preco = precoProdutoCadastrado(p, form.estabelecimentoId);
                          const jaNaCampanha = idsNaCampanha.has(p.id);
                          const marcado = selectedIds.has(p.id);
                          return (
                            <TableRow
                              key={p.id}
                              hover={!jaNaCampanha}
                              selected={marcado}
                              sx={{
                                opacity: jaNaCampanha ? 0.55 : 1,
                                cursor: jaNaCampanha ? 'default' : 'pointer',
                              }}
                              onClick={() => !jaNaCampanha && toggleSelect(p.id)}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={jaNaCampanha || marcado}
                                  disabled={jaNaCampanha}
                                  onChange={() => toggleSelect(p.id)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                              <TableCell>{p.codigo || '-'}</TableCell>
                              <TableCell>{p.descricao}</TableCell>
                              <TableCell align="right">
                                {preco ? fmtMoeda(preco) : '-'}
                              </TableCell>
                              <TableCell align="right">
                                {jaNaCampanha ? (
                                  <Chip size="small" label="Na campanha" variant="outlined" />
                                ) : (
                                  <Button
                                    size="small"
                                    onClick={(e) => { e.stopPropagation(); addItem(p); }}
                                  >
                                    + 1
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </Paper>
                ) : (
                  <Alert severity="info" variant="outlined">
                    {buscando
                      ? 'Buscando produtos...'
                      : 'Nenhum produto na lista. Use a busca acima (espaço no início = busca parcial).'}
                  </Alert>
                )}
              </Box>

              <Box>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Itens da campanha
                  </Typography>
                  <Chip
                    label={`${form.itens.length} produto(s)`}
                    size="small"
                    color={form.itens.length > 0 ? 'primary' : 'default'}
                  />
                </Stack>

                <ResponsiveTable variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 56 }} />
                        <TableCell>Código</TableCell>
                        <TableCell>Descrição</TableCell>
                        {form.tipo === TIPOS.PROMOCAO && <TableCell align="right">R$ promo</TableCell>}
                        {form.tipo === TIPOS.CASHBACK && <TableCell align="right">% cashback</TableCell>}
                        <TableCell align="right" sx={{ width: 56 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.itens.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                            Nenhum produto adicionado. Use a busca acima para selecionar itens.
                          </TableCell>
                        </TableRow>
                      ) : form.itens.map((i, index) => (
                        <TableRow key={i.produtoId} hover>
                          <TableCell data-label="Ordem">
                            <Stack direction="row" spacing={0}>
                              <IconButton
                                size="small"
                                disabled={index === 0}
                                onClick={() => moverItem(index, -1)}
                                aria-label="Subir"
                              >
                                <ArrowUpwardIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={index === form.itens.length - 1}
                                onClick={() => moverItem(index, 1)}
                                aria-label="Descer"
                              >
                                <ArrowDownwardIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          </TableCell>
                          <TableCell data-label="Código">{i.codigo || i.produtoCodigo}</TableCell>
                          <TableCell data-label="Descrição">{i.descricao}</TableCell>

                          {form.tipo === TIPOS.PROMOCAO && (
                            <TableCell align="right" width={180} data-label="R$ promo">
                              <TextField
                                size="small"
                                value={i.precoPromocional}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((prev) => ({
                                    ...prev,
                                    itens: prev.itens.map((x) =>
                                      x.produtoId === i.produtoId ? { ...x, precoPromocional: v } : x),
                                  }));
                                }}
                                placeholder="0,00"
                                helperText={
                                  i.precoCadastrado
                                    ? `Cadastro: ${fmtMoeda(i.precoCadastrado)}`
                                    : undefined
                                }
                                FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
                                InputProps={{
                                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                                }}
                              />
                            </TableCell>
                          )}

                          {form.tipo === TIPOS.CASHBACK && (
                            <TableCell align="right" width={140} data-label="% cashback">
                              <TextField
                                size="small"
                                value={i.cashbackPercent}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setForm((prev) => ({
                                    ...prev,
                                    itens: prev.itens.map((x) =>
                                      x.produtoId === i.produtoId ? { ...x, cashbackPercent: v } : x),
                                  }));
                                }}
                                placeholder="0,00"
                                inputProps={{ inputMode: 'decimal', min: 0, max: 100, step: '0.01' }}
                                InputProps={{
                                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                }}
                              />
                            </TableCell>
                          )}

                          <TableCell align="right" data-actions="">
                            <IconButton color="error" onClick={() => removeItem(i.produtoId)} aria-label="Remover">
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ResponsiveTable>
              </Box>
            </Stack>
          )}
        </Box>
      </Paper>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="center">
        <Button type="submit" variant="contained" disabled={saving} size="large">
          {saving ? 'Gravando...' : 'Gravar campanha'}
        </Button>
        <Button variant="outlined" onClick={fechar} size="large">
          Cancelar
        </Button>
      </Stack>
    </Box>
  );
}
